import { useCallback, useMemo, useState } from 'react';
import { Eye } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody,
  JsonViewer, IconButton,
} from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, StatusPill, Pagination, type ColumnDef, type StatusTone } from '@vnetwork/ui/data';
import { useInvoiceMailHistory, type InvoiceMailTask } from '@/api/invoices';
import { formatDateTime } from '@/lib/format';
import { humanize } from '@/pages/customers/detail/status-tones';
import { paginationView } from '@/pages/customers/pagination-summary';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';

const PAGE_SIZE = 20;

/** Map task_status to a @ui StatusPill tone. */
function mailTaskTone(status?: string): StatusTone {
  switch ((status ?? '').toLowerCase()) {
    case 'completed':
      return 'success';
    case 'processing':
      return 'info';
    case 'pending':
      return 'warning';
    case 'failed':
      return 'danger';
    default:
      return 'neutral';
  }
}

/** Join recipient string (already comma-separated in API) or em-dash. */
function recipients(task: InvoiceMailTask): string {
  const r = task.metadata?.mailer?.recipients;
  return r ? r : '—';
}

/** Metadata viewer dialog for a single task. */
function MetadataDialog({
  task,
  open,
  onOpenChange,
}: {
  task: InvoiceMailTask | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  if (!task) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Metadata — Task #{task.task_id}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <JsonViewer data={task.metadata ?? null} defaultExpandedDepth={2} />
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

function useMailHistoryColumns(onView: (task: InvoiceMailTask) => void) {
  return useMemo<ColumnDef<InvoiceMailTask, unknown>[]>(
    () => [
      {
        id: 'subject',
        header: 'Subject',
        cell: ({ row }) => (
          <span style={{ fontWeight: 'var(--vn-font-weight-medium)', overflowWrap: 'anywhere' }}>
            {row.original.metadata?.subject || '—'}
          </span>
        ),
      },
      {
        id: 'task_status',
        header: 'Status',
        cell: ({ row }) => (
          <StatusPill tone={mailTaskTone(row.original.task_status)}>
            {humanize(row.original.task_status)}
          </StatusPill>
        ),
      },
      {
        id: 'recipients',
        header: 'Recipients',
        cell: ({ row }) => (
          <span style={{ overflowWrap: 'anywhere', color: 'var(--vn-fg-muted)' }}>
            {recipients(row.original)}
          </span>
        ),
      },
      {
        id: 'task_created_at',
        header: 'Created',
        cell: ({ row }) => formatDateTime(row.original.task_created_at),
      },
      {
        id: 'task_completed_at',
        header: 'Completed',
        cell: ({ row }) =>
          row.original.task_completed_at ? formatDateTime(row.original.task_completed_at) : (
            <span style={{ color: 'var(--vn-fg-subtle)' }}>—</span>
          ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <IconButton
            aria-label="View metadata"
            size="sm"
            variant="ghost"
            onClick={() => onView(row.original)}
          >
            <Eye size={15} />
          </IconButton>
        ),
      },
    ],
    [onView],
  );
}

/**
 * Mail History tab — paginated list of `/mailers/tasks/invoices/:id` dispatch
 * tasks. Each row has a "View Metadata" action that opens a dialog with the raw
 * task metadata (subject, mailer recipients, invoice context, etc.).
 * Mirrors `invoice-logs-tab.tsx` for structure, `email-history-section.tsx` for the DataTable pattern.
 */
export function MailHistoryTab({ id }: { id: number }) {
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<InvoiceMailTask | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data, isLoading, isError, refetch } = useInvoiceMailHistory(id, {
    page,
    page_size: PAGE_SIZE,
  });

  const rows = data?.results ?? [];
  const total = data?.pagination?.total ?? rows.length;
  const pageCount = data?.pagination?.total_pages ?? 1;

  const onView = useCallback((task: InvoiceMailTask) => {
    setSelected(task);
    setDialogOpen(true);
  }, []);

  const columns = useMailHistoryColumns(onView);

  if (isError) return <TabError title="Failed to load mail history" onRetry={() => refetch()} />;
  if (isLoading) return <TabLoading />;

  const pv = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);

  return (
    <>
      <DataTable
        columns={columns}
        data={rows}
        enableSorting={false}
        getRowId={(r, i) => String(r.task_id ?? i)}
        emptyState={
          <EmptyState
            title="No mail history"
            description="No email tasks have been dispatched for this invoice yet."
          />
        }
        footer={
          <Pagination
            page={page}
            pageCount={pv.pageCount}
            onPageChange={setPage}
            summary={pv.summary}
          />
        }
      />
      <MetadataDialog task={selected} open={dialogOpen} onOpenChange={setDialogOpen} />
    </>
  );
}
